import { getSentry } from './sentry';

type HomePerformanceData = Record<string, string | number | boolean | null>;

const HOME_PERFORMANCE_PREFIX = 'ora.home';

const getNowSeconds = () =>
  typeof performance !== 'undefined'
    ? (performance.timeOrigin + performance.now()) / 1000
    : Date.now() / 1000;

const mark = (name: string) => {
  if (typeof performance === 'undefined' || !performance.mark) return;
  performance.mark(name);
};

const measure = (name: string, startMark: string, endMark: string) => {
  if (typeof performance === 'undefined' || !performance.measure) return;

  try {
    performance.measure(name, startMark, endMark);
  } catch {
    // Marks can be unavailable in older browsers or after browser buffer cleanup.
  }
};

const getSentrySpanAttributes = (data?: HomePerformanceData) => {
  if (!data) return undefined;

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value === null ? undefined : value,
    ])
  );
};

const recordSentrySpan = (
  name: string,
  data: HomePerformanceData | undefined,
  startTimestamp: number,
  endTimestamp: number
) => {
  if (process.env.NODE_ENV !== 'production') return;

  void getSentry().then((Sentry) => {
    const span = Sentry.startInactiveSpan({
      name,
      op: 'ora.home',
      attributes: getSentrySpanAttributes(data),
      startTime: startTimestamp,
      onlyIfParent: true,
    });

    span.end(endTimestamp);
  });
};

export const recordHomePerformanceEvent = (
  name: string,
  data?: HomePerformanceData
) => {
  const markName = `${HOME_PERFORMANCE_PREFIX}.${name}`;
  const timestamp = getNowSeconds();

  mark(markName);
  recordSentrySpan(name, data, timestamp, timestamp);
};

export const measureHomePerformance = async <T>(
  name: string,
  data: HomePerformanceData | undefined,
  callback: () => Promise<T>
): Promise<T> => {
  const startedAt = getNowSeconds();
  const startMark = `${HOME_PERFORMANCE_PREFIX}.${name}.start`;
  const endMark = `${HOME_PERFORMANCE_PREFIX}.${name}.end`;
  const measureName = `${HOME_PERFORMANCE_PREFIX}.${name}`;

  mark(startMark);

  try {
    return await callback();
  } finally {
    const endedAt = getNowSeconds();
    mark(endMark);
    measure(measureName, startMark, endMark);
    recordSentrySpan(name, data, startedAt, endedAt);
  }
};
